import { useState, useEffect, useRef } from "react";
import { functions, db } from "../firebaseConfig";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { useAuth } from "../AuthContext";
import SideScreen from "../components/SideScreen";
import { BlockButton, BlockButtonWithArrow } from "../components/Buttons";
import PrivacyPolicy from "../components/privacy-policy";
import TermsOfService from "../components/terms-of-service";
import { partnerId } from "../partnerConfig";

import style from "../style/screens/Account.module.css";

interface NoticeType {
    id: string;
    title: string;
    content: string;
    timestamp: number;
    partnerId: string;
}

const Account = () => {
    const { signOutUser } = useAuth();

    const [withdraw, setWithdraw] = useState<boolean>(false);

    const [notice, setNotice] = useState<boolean>(false);
    const [selectedNotice, setSelectedNotice] = useState<NoticeType | null>(null);
    const [noticeDetail, setNoticeDetail] = useState<boolean>(false);
    const [policy, setPolicy] = useState<boolean>(false);
    const [privacy, setPrivacy] = useState<boolean>(false);

    const noticeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        if (Boolean(selectedNotice)) {
            if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
            setNoticeDetail(true);
        }
    }, [selectedNotice]);

    useEffect(() => {
        if (noticeDetail === false) {
            noticeTimeoutRef.current = setTimeout(() => {
                setSelectedNotice(null);
            }, 300);
        }
    }, [noticeDetail]);

    return (
        <div className={style["container"]}>
            <h4 className={style["text--head"]}>계정</h4>
            <div className={style["container--buttons"]}>
                {/* actions */}
                <BlockButtonWithArrow text="로그아웃 하기" onClick={() => signOutUser()} />
                <BlockButtonWithArrow text="계정 탈퇴하기" onClick={() => setWithdraw(true)} />
            </div>
            <hr className={style["hr"]} />
            <div className={style["container--buttons"]}>
                {/* notices */}
                <BlockButtonWithArrow text="공지사항" onClick={() => setNotice(true)} />
            </div>
            <hr className={style["hr"]} />
            <div className={style["container--buttons"]}>
                {/* abouts */}
                <BlockButtonWithArrow text="이용약관 안내" onClick={() => setPolicy(true)} />
                <BlockButtonWithArrow text="개인정보처리방침 안내" onClick={() => setPrivacy(true)} />
            </div>
            {/* side screens */}
            <SideScreen appear={Boolean(withdraw)} title="계정 탈퇴하기" id="A1" close={() => setWithdraw(false)}>
                <Withdraw />
            </SideScreen>
            <SideScreen appear={Boolean(policy)} title="하이비츠 이용약관" id="A2" close={() => setPolicy(false)}>
                <TermsOfService />
            </SideScreen>
            <SideScreen
                appear={Boolean(privacy)}
                title="하이비츠 개인정보처리방침"
                id="A3"
                close={() => setPrivacy(false)}
            >
                <PrivacyPolicy />
            </SideScreen>
            <SideScreen appear={Boolean(notice)} title="공지사항" id="A4" close={() => setNotice(false)}>
                <Notice onSelectNotice={setSelectedNotice} />
            </SideScreen>
            <SideScreen
                appear={noticeDetail}
                title={selectedNotice?.title || "공지사항 상세"}
                id="A5"
                close={() => setNoticeDetail(false)}
            >
                <NoticeDetail notice={selectedNotice} />
            </SideScreen>
        </div>
    );
};

const Withdraw = () => {
    const { signOutUser } = useAuth();
    const [withdrawLoading, setWithdrawLoading] = useState<boolean>(false);

    const accountWithdraw = async () => {
        setWithdrawLoading(true);

        return httpsCallable(functions, "userWithdraw")()
            .then((data) => {
                console.log(data);
                signOutUser();
            })
            .catch((err) => console.error(err));
    };

    return (
        <>
            <div className={style["container--sidescreen"]}>
                <h4 className={style["text--head--sidescreen"]}>계정 탈퇴 전 꼭 확인해주세요!</h4>
                <ol className={style["text--withdraw"]}>
                    <li>
                        탈퇴 시, 모든 데이터가 영구적으로 삭제되며 <b>복구할 수 없습니다.</b>
                        <br />
                        삭제되는 항목:
                        <ul className={style["text--withdraw"]}>
                            <li>이름</li>
                            <li>생년월일</li>
                            <li>핸드폰 번호</li>
                            <li>등록한 키오스크</li>
                            <li>포인트 내역</li>
                            <li>기부 내역</li>
                            <li>환전 내역</li>
                        </ul>
                    </li>
                    <li>동일한 핸드폰 번호로 재가입하여도 기존 데이터를 복구할 수 없습니다.</li>
                    <li>탈퇴 후에는 고객 지원을 통한 데이터 복원이 불가능합니다.</li>
                </ol>
            </div>

            <div className={style["wrapper--footer"]}>
                <BlockButton type="Alert" text="계정 탈퇴하기" loading={withdrawLoading} onClick={accountWithdraw} />
            </div>
        </>
    );
};

const Notice = ({ onSelectNotice }: { onSelectNotice: (notice: NoticeType) => void }) => {
    const [notices, setNotices] = useState<NoticeType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const baseRef = collection(db, "notice");
        const targetQuery = query(baseRef, where("partnerId", "==", partnerId), orderBy("timestamp", "desc"));

        const unsubscribe = onSnapshot(
            targetQuery,
            (snapshot) => {
                const list = snapshot.docs
                    .map((doc) => ({ ...(doc.data() as NoticeType), id: doc.id }))
                    .sort((a, b) => b.timestamp - a.timestamp);
                setNotices(list);
                setLoading(false);
            },
            (error) => {
                console.error("공지사항 조회 오류:", error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [onSelectNotice]);

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    if (loading) {
        return (
            <div className={style["container--sidescreen"]}>
                <p className={style["text--alert"]}>공지사항을 불러오는 중...</p>
            </div>
        );
    }

    if (notices.length === 0) {
        return (
            <div className={style["container--sidescreen"]}>
                <p className={style["text--alert"]}>등록된 공지사항이 없습니다.</p>
            </div>
        );
    }

    return (
        <div className={style["container--sidescreen"]}>
            {notices.map((notice) => (
                <div key={notice.id} style={{ marginBottom: "12px" }}>
                    <p className={style["text--notice--date"]}>{formatDate(notice.timestamp)}</p>
                    <BlockButtonWithArrow text={notice.title || "제목 없음"} onClick={() => onSelectNotice(notice)} />
                </div>
            ))}
        </div>
    );
};

const NoticeDetail = ({ notice }: { notice: NoticeType | null }) => {
    if (!notice) return null;

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    return (
        <div className={style["container--sidescreen"]}>
            <div style={{ marginBottom: "16px" }}>
                <h4 className={style["text--head--sidescreen"]}>{notice.title}</h4>
                <p className={style["text--notice-detail--date"]}>{formatDate(notice.timestamp)}</p>
            </div>
            <div className={style["container--notice"]} dangerouslySetInnerHTML={{ __html: notice.content || "" }} />
        </div>
    );
};

export default Account;
